import React from 'react'
import { useToast } from '@vtex/admin-ui'
import { Route, Switch, useHistory, useRouteMatch } from 'react-router'
import { useIntl } from 'react-intl'

import { useCheckout } from '../../state'
import type { ClientDataState } from '../../typings'
import {
  ConfirmPostalCodeStep,
  NameStep,
  DocumentStep,
  PhoneStep,
  CompanyNameStep,
  TradeNameStep,
  CorporatePhoneStep,
  CorporateDocumentStep,
  ClientProfileContainer,
  EmailStepContainer,
  StateInscriptionStep,
} from '../../components'
import { CheckoutRoute, PURCHASE_FLOW_ORIGIN_HASH } from '../../constants'
import { isEditMode } from '../../utils/verifyEditionMode'
import { getOrigin } from '../../utils/getOrigin'
import { isClientProfileComplete } from '../../utils'
import type { OrderForm } from '../../../../core/requests/checkout'
import { TOAST_DEFAULT_DURATION_MS } from '../../../../core/constants'
import { getClientDataStateFromOrderForm } from '../../utils/getClientDataStateFromOrderForm'
import {
  ConfirmAddressStepContainer,
  HomeDeliveryAddressSteps,
} from '../HomeDelivery/components'
import { TopbarMenuArea } from '../../../Instore/components/Header/Topbar'
import { BackButton } from '../../../../core/components'

export const EditProfileScreen = () => {
  const { path } = useRouteMatch()
  const history = useHistory()
  const checkout = useCheckout()
  const { clientData } = checkout.checkout

  const showToast = useToast()
  const { formatMessage } = useIntl()
  const isEditing = isEditMode()
  const origin = getOrigin() ?? ''
  const isEditingClientProfile = path === '/checkout/edit-profile' // TODO: remove when develop new client profile screen

  const handleNameSubmit = (name: ClientDataState['name']) => {
    checkout.setClientDataName(name)

    history.push(
      isEditing
        ? `${PURCHASE_FLOW_ORIGIN_HASH[origin]}`
        : CheckoutRoute.EditProfileDocument
    )
  }

  const handleDocumentSubmit = (document: ClientDataState['document']) => {
    checkout.setClientDataDocument(document)

    history.push(
      isEditing
        ? `${PURCHASE_FLOW_ORIGIN_HASH[origin]}`
        : CheckoutRoute.EditProfilePhone
    )
  }

  const handlePhoneSubmit = (phone: ClientDataState['phone']) => {
    checkout.setClientDataPhone(phone)

    const nextStep = isEditing
      ? `${PURCHASE_FLOW_ORIGIN_HASH[origin]}`
      : CheckoutRoute.EditProfileClientProfile

    history.push(nextStep)
  }

  const handleConfirmClientSubmit = () => {
    if (isEditingClientProfile) {
      return history.push(CheckoutRoute.EditProfileConfirmAdress)
    }

    if (!clientData.address.neighborhood) {
      return history.push(CheckoutRoute.EditProfileAddressNeighborhood)
    }

    history.push(CheckoutRoute.EditProfileAddressNumber)
  }

  const handleConfirmAddressSubmit = () => {
    if (isEditingClientProfile) {
      showToast({
        key: 'profile-edit-success',
        variant: 'positive',
        message: formatMessage({
          id: 'checkout.edit-profile.edit-success',
        }),
        duration: TOAST_DEFAULT_DURATION_MS,
      })

      return history.push('/checkout/cart')
    }

    history.push('../order-summary')
  }

  const handlePostalCodeSubmit = () => {
    history.push(CheckoutRoute.Name)
  }

  const handleCompanyNameSubmit = (
    companyName: ClientDataState['companyInformation']['companyName']
  ) => {
    checkout.setClientDataCompanyName(companyName)

    history.push(
      isEditing
        ? CheckoutRoute.EditProfileClientProfile
        : CheckoutRoute.EditProfileTradeName
    )
  }

  const handleTradeNameSubmit = (
    tradeName: ClientDataState['companyInformation']['tradeName']
  ) => {
    checkout.setClientDataTradeName(tradeName)

    history.push(
      isEditing
        ? CheckoutRoute.EditProfileClientProfile
        : CheckoutRoute.EditProfileCorporatePhone
    )
  }

  const handleCorporatePhoneSubmit = (
    corporatePhone: ClientDataState['companyInformation']['corporatePhone']
  ) => {
    checkout.setClientDataCorporatePhone(corporatePhone)

    history.push(
      isEditing
        ? CheckoutRoute.EditProfileClientProfile
        : CheckoutRoute.EditProfileCorporateDocument
    )
  }

  const handleCorporateDocumentSubmit = (
    corporateDocument: ClientDataState['companyInformation']['corporateDocument']
  ) => {
    checkout.setClientDataCorporateDocument(corporateDocument)

    history.push(
      isEditing
        ? CheckoutRoute.EditProfileClientProfile
        : CheckoutRoute.EditProfileStateInscription
    )
  }

  const handleEmailSubmit = async (orderFormHandled: OrderForm | null) => {
    if (!orderFormHandled) {
      return
    }

    const submittedEmail = orderFormHandled.clientProfileData?.email ?? ''

    if (!isClientProfileComplete(orderFormHandled.clientProfileData)) {
      if (clientData.email !== submittedEmail) {
        checkout.clearClientDataFromClientDataState()
      }

      checkout.setClientDataEmail(submittedEmail)
      const nextStep = orderFormHandled.shippingData?.address
        ? CheckoutRoute.EditProfileName
        : CheckoutRoute.EditProfileConfirmPostalCode

      history.push(nextStep)

      return
    }

    checkout.setClientDataState(
      getClientDataStateFromOrderForm(orderFormHandled)
    )

    history.push(CheckoutRoute.EditProfileClientProfile)
  }

  const handleStateInscriptionSubmit = (
    stateInscription: ClientDataState['companyInformation']['stateInscription']
  ) => {
    checkout.setClientDataStateIncription(stateInscription)
    checkout.setClientDataIsCorporate(true)

    history.push(CheckoutRoute.EditProfileClientProfile)
  }

  return (
    <>
      <TopbarMenuArea>
        <BackButton />
      </TopbarMenuArea>
      <Switch>
        <Route path={CheckoutRoute.EditProfileEmail}>
          <EmailStepContainer
            currentState={clientData}
            onStepSubmit={handleEmailSubmit}
          />
        </Route>
        <Route path={CheckoutRoute.EditProfileName}>
          <NameStep
            isEditing={isEditing}
            data-testid="name-step"
            onStepSubmit={handleNameSubmit}
            initialName={clientData.name}
          />
        </Route>
        <Route path={CheckoutRoute.EditProfileDocument}>
          <DocumentStep
            isEditing={isEditing}
            onStepSubmit={handleDocumentSubmit}
            initialDocument={clientData.document}
            clientName={clientData.name}
          />
        </Route>
        <Route path={CheckoutRoute.EditProfilePhone}>
          <PhoneStep
            isEditing={isEditing}
            onStepSubmit={handlePhoneSubmit}
            clientName={clientData.name}
            initialPhone={clientData.phone}
          />
        </Route>
        <Route path={CheckoutRoute.EditProfileClientProfile}>
          <ClientProfileContainer nextStep={handleConfirmClientSubmit} />
        </Route>
        <Route path={CheckoutRoute.EditProfileCompanyName}>
          <CompanyNameStep
            isEditing={isEditing}
            data-testid="company-name-step"
            onStepSubmit={handleCompanyNameSubmit}
            initialName={clientData.companyInformation.companyName}
          />
        </Route>
        <Route path={CheckoutRoute.EditProfileTradeName}>
          <TradeNameStep
            isEditing={isEditing}
            data-testid="trade-name-step"
            onStepSubmit={handleTradeNameSubmit}
            initialName={clientData.companyInformation.tradeName}
          />
        </Route>
        <Route path={CheckoutRoute.EditProfileCorporatePhone}>
          <CorporatePhoneStep
            isEditing={isEditing}
            onStepSubmit={handleCorporatePhoneSubmit}
            initialPhone={clientData.companyInformation.corporatePhone}
          />
        </Route>
        <Route path={CheckoutRoute.EditProfileCorporateDocument}>
          <CorporateDocumentStep
            isEditing={isEditing}
            onStepSubmit={handleCorporateDocumentSubmit}
            initialDocument={clientData.companyInformation.corporateDocument}
          />
        </Route>
        <Route path={CheckoutRoute.EditProfileStateInscription}>
          <StateInscriptionStep
            isEdit={isEditing}
            onStepSubmit={handleStateInscriptionSubmit}
            initialStateInscription={
              clientData.companyInformation.stateInscription
            }
          />
        </Route>
        <Route path={CheckoutRoute.EditProfileConfirmAdress}>
          <ConfirmAddressStepContainer
            onStepSubmit={handleConfirmAddressSubmit}
          />
        </Route>
        <Route path={CheckoutRoute.EditProfileConfirmPostalCode}>
          <ConfirmPostalCodeStep
            data-testid="confirm-postal-code"
            onStepSubmit={handlePostalCodeSubmit}
          />
        </Route>
        <Route path={CheckoutRoute.EditProfileAddressSteps}>
          <HomeDeliveryAddressSteps />
        </Route>
      </Switch>
    </>
  )
}
